/**
 * Mapper for the "legal document updated" modal.
 *
 * One pure lookup keyed off `VersionUpdatedLegalDocument`, colocated with
 * `LegalDocumentUpdatedModal` (its only consumer): `getLegalDocumentNavigation`
 * — the `Navigator` call that opens that document's public legal page.
 *
 * Flutter source of truth: `lib/presentation/utils/legal_documents_utils.dart:22-63`
 * — the `NavigationService.navigateTo…Screen()` per member, which maps 1:1 onto
 * the web `Navigator` methods below (`Navigator.ts:281-339`).
 *
 * The matching user-facing document *name* is not here: it is a value-conditional
 * translation lookup, so it lives with its siblings in
 * `useExpauseLocalizations.getLegalDocumentLabel`
 * (`presentation/locale/expauseLocalizations.ts`), which also records why the
 * labels reuse the existing `l10n.legal.*` names.
 *
 * The switch deliberately has **no `default`**: the function declares a
 * non-`void` return type, so under `strict` a future eighth enum member fails
 * the type-check ("function lacks ending return statement") instead of silently
 * navigating nowhere.
 */

import { VersionUpdatedLegalDocument } from '@/domain/enums/versionUpdatedLegalDocument';
import { Navigator } from '@/presentation/routing/Navigator';

/**
 * The navigation to run when the document link is tapped.
 *
 * Returns the call rather than performing it so the switch has a non-`void`
 * return type and stays exhaustiveness-checked (see the file header).
 */
export const getLegalDocumentNavigation = (
  legalDocument: VersionUpdatedLegalDocument,
): (() => void) => {
  switch (legalDocument) {
    case VersionUpdatedLegalDocument.termsOfService:
      return () => Navigator.navigateToTermsOfUse();
    case VersionUpdatedLegalDocument.coinsPolicy:
      return () => Navigator.navigateToCoinsPolicy();
    case VersionUpdatedLegalDocument.communityGuidelines:
      return () => Navigator.navigateToCommunityGuidelines();
    case VersionUpdatedLegalDocument.exclusiveSubscriptionsPolicy:
      return () => Navigator.navigateToExclusiveSubscriptionsPolicy();
    case VersionUpdatedLegalDocument.premiumSubscriptionsPolicy:
      return () => Navigator.navigateToPremiumSubscriptionsPolicy();
    case VersionUpdatedLegalDocument.privacyPolicy:
      return () => Navigator.navigateToPrivacyPolicy();
    case VersionUpdatedLegalDocument.rewardsPolicy:
      return () => Navigator.navigateToRewardsPolicy();
  }
};
